import roadmapThumbnailSrc from '@assets/images/roadmap.png';
import { Button, cn, Container, Image, Stack } from '@landler/component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { usePathSet } from '@/hooks/usePathSet';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { ComingSoon } from '../components/ComingSoon';
import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../hooks/useIsOrganizationMember';
import { useOrganizationId } from '../hooks/useOrganizationId';
import { useOrganizationRoadmapNodes } from '../hooks/useOrganizationRoadmapNodes';
import { EditRoadmapNode } from './components/EditRoadmapNode';
import { UploadRoadmapNode } from './components/UploadRoadmapNode';

export const GoalsPage = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();
  const navigate = useNavigate();

  const isStaffUser = useIsStaffUser();
  const organizationId = useOrganizationId();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const hideGoals = isLimitedAccessUser || !isOrganizationMember;

  const [showAddRoadmap, setShowAddRoadmap] = useState<boolean>(false);

  return (
    <SignedInPageLayout
      items={[]}
      navBarProps={{
        title: t('navbar.goals'),
        showFilters: false,
        actionButton: !hideGoals
          ? {
              text: t('global.copilot.configureCopilot'),
              leftAdornment: <Icon icon='ri-settings-2-line' />,
              onClick: () => {
                navigate(
                  buildPath(pathSet.organizationOnboarding, {
                    pathParams: {
                      organizationId,
                      onboardingSection: 1,
                    },
                  }),
                  {
                    state: {
                      from: pathSet.goals,
                    },
                  },
                );
              },
            }
          : undefined,
        children: !hideGoals ? (
          <Stack direction='row' className='justify-end gap-4'>
            {isStaffUser && (
              <Button
                data-testid='add-roadmap-button'
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddRoadmap(!showAddRoadmap);
                }}
                variant='outline'
                leftAdornment={<Icon icon={showAddRoadmap ? 'ri-subtract-line' : 'ri-add-large-line'} />}
                className='px-3 py-3 rounded-3xl border border-neutral-black-12'
              >
                {t('shared.goals.editNode.addRoadmap')}
              </Button>
            )}
          </Stack>
        ) : null,
      }}
    >
      <Container gutterWidth={{ sm: 2 }} className='gap-10 my-8 ' data-testid='goals-page'>
        <Stack spacing={10}>
          {hideGoals ? (
            <ComingSoon />
          ) : (
            <>
              {isStaffUser && showAddRoadmap && (
                <Stack className='flex flex-col md:flex-row gap-8 w-full justify-between items-center mb-8'>
                  <EditRoadmapNode
                    organizationId={organizationId}
                    onSuccess={() => setShowAddRoadmap(false)}
                    onClose={() => setShowAddRoadmap(false)}
                    title={t('shared.goals.editNode.addRoadmap')}
                  />
                  <span className='uppercase typography-body1 text-text-secondary self-center'>
                    {t('global.compare.or')}
                  </span>
                  <UploadRoadmapNode organizationId={organizationId} />
                </Stack>
              )}
              <Roadmaps />
            </>
          )}
        </Stack>
      </Container>
    </SignedInPageLayout>
  );
};

const Roadmaps = () => {
  const navigate = useNavigate();
  const pathSet = usePathSet();

  const isStaffUser = useIsStaffUser();

  let roadmaps = useOrganizationRoadmapNodes(undefined, { parent_id: 'null' }).data;

  if (!isStaffUser) {
    roadmaps = roadmaps.filter((roadmap) => roadmap.visible_to_customer);
  }

  if (roadmaps.length === 0 && !isStaffUser) {
    return <ComingSoon />;
  }

  return (
    <Stack className='gap-6 justify-start items-start' data-testid='roadmaps-list'>
      {roadmaps.map(
        (roadmap) =>
          !isNullish(roadmap) &&
          !isNullish(roadmap.id) && (
            <Stack
              key={roadmap.id}
              className='w-full flex flex-row gap-4 p-2 cursor-pointer border border-neutral-black-12 rounded-2xl'
              onClick={() => {
                navigate(buildPath(pathSet.roadmap, { pathParams: { roadmapId: roadmap.id ?? '' } }));
              }}
            >
              <Image
                alt='roadmap-thumbnail'
                fallback={roadmapThumbnailSrc}
                src={roadmapThumbnailSrc}
                className='bg-secondary-30 object-cover h-[200px] min-w-[200px] rounded-2xl'
              />
              <Stack className='gap-6 p-3'>
                <Stack className='gap-2'>
                  <span
                    className={cn(
                      'typography-h4 whitespace-normal',
                      !roadmap.visible_to_customer && 'text-text-disabled',
                    )}
                  >
                    {roadmap.title}
                  </span>
                  <span
                    className={cn(
                      'typography-body1 text-text-secondary whitespace-normal',
                      !roadmap.visible_to_customer && 'text-text-disabled',
                    )}
                  >
                    {roadmap.description}
                  </span>
                </Stack>
              </Stack>
            </Stack>
          ),
      )}
    </Stack>
  );
};
